import React, { forwardRef, useEffect, useImperativeHandle, useRef } from 'react'

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY
const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit'

export const isCaptchaEnabled = !!SITE_KEY

let scriptPromise
const loadScript = () => {
  if (window.turnstile) return Promise.resolve()
  if (!scriptPromise) {
    scriptPromise = new Promise((resolve, reject) => {
      const script = document.createElement('script')
      script.src = SCRIPT_SRC
      script.async = true
      script.onload = resolve
      script.onerror = () => {
        scriptPromise = null
        reject(new Error('Failed to load captcha'))
      }
      document.head.appendChild(script)
    })
  }
  return scriptPromise
}

// Cloudflare Turnstile widget. Renders nothing when REACT_APP_TURNSTILE_SITE_KEY is unset.
// Tokens are single-use: call ref.current.reset() after every submit attempt.
const TurnstileCaptcha = forwardRef(({ onToken }, ref) => {
  const containerRef = useRef(null)
  const widgetIdRef = useRef(null)
  const onTokenRef = useRef(onToken)
  onTokenRef.current = onToken

  useImperativeHandle(ref, () => ({
    reset: () => {
      onTokenRef.current(null)
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.reset(widgetIdRef.current)
      }
    },
  }))

  useEffect(() => {
    if (!isCaptchaEnabled) return undefined
    let cancelled = false
    loadScript()
      .then(() => {
        if (cancelled || !containerRef.current) return
        widgetIdRef.current = window.turnstile.render(containerRef.current, {
          sitekey: SITE_KEY,
          callback: token => onTokenRef.current(token),
          'expired-callback': () => onTokenRef.current(null),
          'error-callback': () => onTokenRef.current(null),
        })
      })
      .catch(() => onTokenRef.current(null))
    return () => {
      cancelled = true
      if (widgetIdRef.current !== null && window.turnstile) {
        window.turnstile.remove(widgetIdRef.current)
        widgetIdRef.current = null
      }
    }
  }, [])

  if (!isCaptchaEnabled) return null
  return <div ref={containerRef} className='turnstileCaptcha' style={{ margin: '12px 0' }} />
})

export default TurnstileCaptcha
